import React, { useState, useEffect, useMemo } from "react";
import {
    Box, TextField, Button, Typography, Divider,
    List, ListItemButton, ListItemText, Paper,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem,
    FormControlLabel, Checkbox
} from "@mui/material";
import { db } from "../../firebase";
import { 
    collection, getDocs, query, where, Timestamp, orderBy, 
    writeBatch, doc 
} from "firebase/firestore";

function CustomerwiseReport() {
    const today = new Date().toISOString().split("T")[0];
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [fetchAllData, setFetchAllData] = useState(false); // Toggle for All-Time Data vs Date Range
    const [allTrips, setAllTrips] = useState([]);
    const [allPayments, setAllPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);

    // Transfer State
    const [transferDialogOpen, setTransferDialogOpen] = useState(false);
    const [targetCustomerId, setTargetCustomerId] = useState("");
    const [transferring, setTransferring] = useState(false);

    const fetchBaseData = async () => {
        setLoading(true);
        try {
            let qTrips, qPay;

            if (fetchAllData) {
                // Fetch ALL-TIME data without date constraints
                qTrips = query(collection(db, "trips"), orderBy("grossDate", "asc"));
                qPay = query(collection(db, "customer_history"), orderBy("date", "asc"));
            } else {
                // Fetch data filtered by Date Range constraints
                const start = Timestamp.fromDate(new Date(fromDate + " 00:00:00"));
                const end = Timestamp.fromDate(new Date(toDate + " 23:59:59"));

                qTrips = query(
                    collection(db, "trips"),
                    where("grossDate", ">=", start),
                    where("grossDate", "<=", end),
                    orderBy("grossDate", "asc")
                );

                qPay = query(
                    collection(db, "customer_history"),
                    where("date", ">=", start),
                    where("date", "<=", end),
                    orderBy("date", "asc")
                );
            }

            const [tripSnap, paySnap] = await Promise.all([getDocs(qTrips), getDocs(qPay)]);

            const trips = tripSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                calculatedTotal: (doc.data().products || []).reduce((acc, p) => acc + (Number(p.amount) || 0), 0)
            }));

            const payments = paySnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setAllTrips(trips);
            setAllPayments(payments);

            // Automatically select the first customer if none is selected
            const availableCustomers = [...new Set(trips.map(t => t.customerId))];
            if (availableCustomers.length > 0 && (!selectedCustomerId || !availableCustomers.includes(selectedCustomerId))) {
                setSelectedCustomerId(availableCustomers[0]);
            } else if (availableCustomers.length === 0) {
                setSelectedCustomerId(null);
            }
        } catch (err) {
            console.error("Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    // Generate sorted unique customer list from fetched trips
    const customerList = useMemo(() => {
        const custMap = {};
        allTrips.filter(t => t.customerId).forEach(t => {
            if (!custMap[t.customerId]) {
                custMap[t.customerId] = t.customerName || "Unknown Customer";
            }
        });
        return Object.entries(custMap)
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [allTrips]);

    // Filter trips and payments strictly for the currently selected customer
    const reportData = useMemo(() => {
        if (!selectedCustomerId) return { trips: [], payments: [], customerName: "N/A" };
        const filteredTrips = allTrips.filter(t => t.customerId === selectedCustomerId);
        const filteredPayments = allPayments.filter(p => p.customerId === selectedCustomerId);
        return {
            trips: filteredTrips,
            payments: filteredPayments,
            customerName: filteredTrips[0]?.customerName || customerList.find(c => c.id === selectedCustomerId)?.name || "N/A"
        };
    }, [selectedCustomerId, allTrips, allPayments, customerList]);

    useEffect(() => { 
        fetchBaseData(); 
    }, [fetchAllData]);

    // --- Transfer Logic ---
    const handleTransferData = async () => {
        if (!targetCustomerId || targetCustomerId === selectedCustomerId) return;
        
        setTransferring(true);
        try {
            const targetCustomerName = customerList.find(c => c.id === targetCustomerId)?.name || "Unknown Customer";
            const batch = writeBatch(db);

            const tripsQ = query(collection(db, "trips"), where("customerId", "==", selectedCustomerId));
            const tripsSnap = await getDocs(tripsQ);
            
            tripsSnap.forEach((tripDoc) => {
                const tripRef = doc(db, "trips", tripDoc.id);
                batch.update(tripRef, {
                    customerId: targetCustomerId,
                    customerName: targetCustomerName
                });
            });

            const payQ = query(collection(db, "customer_history"), where("customerId", "==", selectedCustomerId));
            const paySnap = await getDocs(payQ);
            
            paySnap.forEach((payDoc) => {
                const payRef = doc(db, "customer_history", payDoc.id);
                batch.update(payRef, {
                    customerId: targetCustomerId,
                    customerName: targetCustomerName
                });
            });

            await batch.commit();
            
            alert("All records transferred successfully!");
            setTransferDialogOpen(false);
            setTargetCustomerId("");
            
            await fetchBaseData();
            setSelectedCustomerId(targetCustomerId);
            
        } catch (error) {
            console.error("Transfer Error:", error);
            alert("Error transferring data. See console for details.");
        } finally {
            setTransferring(false);
        }
    };

    const cellStyle = { border: '1px solid #000', padding: '6px', fontSize: '0.8rem' };
    const headerStyle = { ...cellStyle, fontWeight: 'bold', backgroundColor: '#f2f2f2', textAlign: 'center' };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f5f5f5' }}>
            {/* Top Bar */}
            <Paper elevation={1} sx={{ p: 2, mb: 1, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }} className="no-print">
                <FormControlLabel
                    control={
                        <Checkbox 
                            checked={fetchAllData} 
                            onChange={e => setFetchAllData(e.target.checked)} 
                        />
                    }
                    label="All Data"
                />
                <TextField 
                    size="small" 
                    label="From" 
                    type="date" 
                    InputLabelProps={{ shrink: true }} 
                    value={fromDate} 
                    onChange={e => setFromDate(e.target.value)} 
                    disabled={fetchAllData}
                />
                <TextField 
                    size="small" 
                    label="To" 
                    type="date" 
                    InputLabelProps={{ shrink: true }} 
                    value={toDate} 
                    onChange={e => setToDate(e.target.value)} 
                    disabled={fetchAllData}
                />
                <Button variant="contained" onClick={fetchBaseData} disabled={loading}>
                    {loading ? "..." : "Load Data"}
                </Button>
                <Button variant="outlined" color="secondary" onClick={() => window.print()}>Print PDF</Button>
            </Paper>

            <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                {/* Left Sidebar */}
                <Paper sx={{ width: 260, mr: 1, display: 'flex', flexDirection: 'column' }} className="no-print">
                    <Typography variant="button" sx={{ p: 2, bgcolor: '#444', color: '#fff' }}>Customer List</Typography>
                    <List sx={{ overflowY: 'auto', flexGrow: 1 }}>
                        {customerList.map((cust) => (
                            <ListItemButton
                                key={cust.id}
                                selected={selectedCustomerId === cust.id}
                                onClick={() => setSelectedCustomerId(cust.id)}
                            >
                                <ListItemText primary={cust.name} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                            </ListItemButton>
                        ))}
                    </List>
                </Paper>

                {/* Report Content */}
                <Box sx={{ flexGrow: 1, bgcolor: '#fff', p: "20px", overflowY: 'auto', position: 'relative' }} className="printable-area">
                    {selectedCustomerId ? (
                        <Box sx={{ width: '100%' }}>
                            
                            {/* Transfer Button (Hidden in Print) */}
                            <Box className="no-print" sx={{ position: 'absolute', top: 50, right: 20 }}>
                                <Button 
                                    variant="outlined" 
                                    color="error" 
                                    size="small"
                                    onClick={() => setTransferDialogOpen(true)}
                                >
                                    Transfer Data
                                </Button>
                            </Box>

                            <Box textAlign="center" mb={2}>
                                <Divider sx={{ my: 2, borderBottomWidth: 2, borderColor: '#000' }} />
                                <Typography variant="h6" fontWeight="bold">CUSTOMER STATEMENT: {reportData.customerName.toUpperCase()}</Typography>
                                <Typography variant="body2">
                                    {fetchAllData ? "Date Range: All Time Records" : `Date Range: ${fromDate} to ${toDate}`}
                                </Typography>
                            </Box>

                            {/* Section I */}
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>I. TRIP SALES LOG</Typography>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...headerStyle, width: '40px' }}>Sr.</th>
                                        <th style={headerStyle}>Ticket</th>
                                        <th style={headerStyle}>Date</th>
                                        <th style={headerStyle}>Chalan No</th>
                                        <th style={headerStyle}>Vehicle</th>
                                        <th style={{ ...headerStyle, textAlign: 'left' }}>Product Description</th>
                                        <th style={headerStyle}>Qty</th>
                                        <th style={headerStyle}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.trips.map((row, index) => (
                                        <tr key={row.id}>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{index + 1}</td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{row.ticketNo}</td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                {row.grossDate?.toDate().toLocaleDateString('en-GB')}
                                            </td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{row.challan}</td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{row.vehicleNo}</td>

                                            {/* PRODUCT COLUMN */}
                                            <td style={{ ...cellStyle, textAlign: 'left' }}>
                                                {row.products?.map((p, i) => (
                                                    <div key={i} style={{ borderBottom: i < row.products.length - 1 ? '1px solid #eee' : 'none' }}>
                                                        {p.productName}
                                                    </div>
                                                ))}
                                            </td>

                                            {/* QTY COLUMN */}
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                {row.products?.map((p, i) => (
                                                    <div key={i} style={{ borderBottom: i < row.products.length - 1 ? '1px solid #eee' : 'none', fontWeight: '500' }}>
                                                        {p.qty || p.brass || 0}
                                                    </div>
                                                ))}
                                            </td>

                                            <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                                                ₹{row.calculatedTotal.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}

                                    {/* TOTAL FOOTER */}
                                    <tr style={{ backgroundColor: '#f9f9f9' }}>
                                        <td colSpan={7} style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>TOTAL SALES</td>
                                        <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '0.9rem' }}>
                                            ₹{reportData.trips.reduce((acc, curr) => acc + curr.calculatedTotal, 0).toLocaleString()}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* Section II */}
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 4, mb: 1 }}>II. COLLECTION LOG</Typography>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...headerStyle, width: '40px' }}>Sr.</th>
                                        <th style={headerStyle}>Date</th>
                                        <th style={headerStyle}>Mode</th>
                                        <th style={headerStyle}>Note</th>
                                        <th style={headerStyle}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.payments.map((row, index) => (
                                        <tr key={row.id}>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{index + 1}</td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{row.date?.toDate().toLocaleDateString('en-GB')}</td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{row.paymentMode}</td>
                                            <td style={cellStyle}>{row.note || "-"}</td>
                                            <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>₹{(Number(row.amount) || 0).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ backgroundColor: '#f9f9f9' }}>
                                        <td colSpan={4} style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>TOTAL RECEIVED</td>
                                        <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                                            ₹{reportData.payments.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0).toLocaleString()}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </Box>
                    ) : (
                        <Typography variant="body1" textAlign="center" mt={10}>Please select a customer from the sidebar.</Typography>
                    )}
                </Box>
            </Box>

            {/* Transfer Confirmation Dialog */}
            <Dialog open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Transfer Customer Data</DialogTitle>
                <DialogContent>
                    <Typography mb={3} mt={1}>
                        You are about to transfer all trips and collections from <b>{reportData.customerName}</b> to another customer. 
                        This action will move all database records and cannot be undone.
                    </Typography>
                    <FormControl fullWidth size="small">
                        <InputLabel>Target Customer</InputLabel>
                        <Select
                            value={targetCustomerId}
                            label="Target Customer"
                            onChange={(e) => setTargetCustomerId(e.target.value)}
                        >
                            {customerList
                                .filter((c) => c.id !== selectedCustomerId)
                                .map((cust) => (
                                    <MenuItem key={cust.id} value={cust.id}>
                                        {cust.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setTransferDialogOpen(false)} color="inherit" disabled={transferring}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        color="error" 
                        onClick={handleTransferData} 
                        disabled={!targetCustomerId || transferring}
                    >
                        {transferring ? "Transferring..." : "Confirm Transfer"}
                    </Button>
                </DialogActions>
            </Dialog>

            <style>{`
                @media print {
                    @page { size: A4; margin: 15mm; }
                    .no-print { display: none !important; }
                    body { background: none; padding: 0; margin: 0; }
                    .printable-area { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 100%; 
                        padding: 0;
                        visibility: visible;
                        padding: 20px;
                    }
                    * { visibility: hidden; }
                    .printable-area, .printable-area * { visibility: visible; }
                }
            `}</style>
        </Box>
    );
}

export default CustomerwiseReport;